/**
 * ReportShareSheetContainer - Manages share logic for scout reports
 *
 * Delegates common share modal behavior (user fetching, search, multi-select)
 * to the shared useShareModal hook, and manager display/auto-share logic to
 * useManagerShareRecipients. Adds report-specific functionality: fetching
 * existing share recipients, excluding already-shared users from the
 * selection list, filtering out the owner from the visible recipients,
 * and supporting access revocation.
 */

import { useCallback, useMemo } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import ShareModalView from "@/components/organisms/shared/ShareModalView";
import { useShareModal } from "@/components/organisms/shared/useShareModal";
import { useManagerShareRecipients } from "@/components/organisms/shared/useShareModal/useManagerShareRecipients";
import { Box } from "@/components/atoms/Box";
import ShareRecipientsList from "@/features/reports/components/molecules/ShareRecipientsList";
import { useCurrentUser } from "@/features/auth";
import {
  RoleName,
  useReportsListScoutReportSharesQuery,
  useReportsRevokeScoutReportShareMutation,
  useReportsShareScoutReportMutation,
} from "@/generated/graphql";
import { useToastNotification } from "@/hooks/useToastNotification";
import { formatError } from "@/utils/formatError";

/** Props for ReportShareSheetContainer */
interface Props {
  /** ID of the scout report to share */
  readonly scoutReportId: string;
  /** Whether the sheet is visible */
  readonly isOpen: boolean;
  /** Callback when the sheet is dismissed */
  readonly onClose: () => void;
}

/**
 * Returns the border class for the existing shares section divider
 * @remarks SE-6800b: dark:border-zinc-700/light:border-zinc-200 collapsed to
 * a single border-outline-subtle token (same mapping as
 * ShareRecipientsListView's getBorderClass; worst=5, Δlight=[2,2,-1],
 * Δdark=[2,2,-5]).
 */
const BORDER_CLASS = "border-outline-subtle";

/**
 * Container for the report share modal - uses the shared useShareModal hook
 * for common logic and adds report-specific shares listing and revocation.
 * @param props - Component props
 * @param props.scoutReportId - ID of the scout report to share
 * @param props.isOpen - Whether the sheet is visible
 * @param props.onClose - Callback when the sheet is dismissed
 */
const ReportShareSheetContainer = ({
  scoutReportId,
  isOpen,
  onClose,
}: Props) => {
  const { top } = useSafeAreaInsets();
  const toast = useToastNotification();
  const currentUser = useCurrentUser();
  const isMember = currentUser?.role?.name === RoleName.Member;

  const [shareReport] = useReportsShareScoutReportMutation({
    refetchQueries: ["ReportsListScoutReportShares"],
  });

  const { data: sharesData, loading: sharesLoading } =
    useReportsListScoutReportSharesQuery({
      variables: { scoutReportId },
      skip: !isOpen || !scoutReportId,
      fetchPolicy: "network-only",
    });

  const [revokeShare, { loading: isRevoking }] =
    useReportsRevokeScoutReportShareMutation();

  const allShares = useMemo(
    () => sharesData?.listScoutReportShares ?? [],
    [sharesData]
  );

  const existingShareUserIds = useMemo(
    () => allShares.map(share => share.userId),
    [allShares]
  );

  const visibleShares = useMemo(
    () => allShares.filter(share => share.role !== "OWNER"),
    [allShares]
  );

  const shareFn = useCallback(
    (targetUserId: string) =>
      shareReport({
        variables: { input: { scoutReportId, targetUserId } },
      }),
    [shareReport, scoutReportId]
  );

  const modal = useShareModal({
    isOpen,
    onClose,
    entityId: scoutReportId,
    shareFn,
    entityLabel: "Report",
    excludeUserIds: existingShareUserIds,
    ...(isMember && { keepOrgRoles: ["MANAGER", "ADMIN"] }),
  });

  const {
    nonManagerShares,
    managerRecipients,
    shareWithUnsharedManagers: handleShareWithManagers,
  } = useManagerShareRecipients({
    allOrgUsers: modal.allOrgUsers,
    existingShareUserIds,
    modalOnShare: modal.onShare,
    shareWithUser: shareFn,
    visibleShares,
  });

  const handleRevoke = useCallback(
    async (targetUserId: string) => {
      if (!scoutReportId) return;
      try {
        await revokeShare({
          variables: { input: { scoutReportId, targetUserId } },
          refetchQueries: ["ReportsListScoutReportShares"],
        });
        toast.show({
          description: "Access revoked",
          action: "success",
        });
      } catch (error) {
        toast.show({
          description: formatError(error)?.message ?? "Failed to revoke access",
          action: "danger",
        });
      }
    },
    [scoutReportId, revokeShare, toast]
  );

  return (
    <ShareModalView
      {...modal}
      onShare={handleShareWithManagers}
      onClose={modal.onCancel}
      subtitle="Select teammates who should be able to view this report."
      topSafeArea={top}
    >
      {!sharesLoading &&
        (nonManagerShares.length > 0 || managerRecipients.length > 0) && (
          <Box className={`border-t pt-4 ${BORDER_CLASS}`}>
            <ShareRecipientsList
              shares={nonManagerShares}
              additionalRecipients={managerRecipients}
              onRevoke={handleRevoke}
              isRevoking={isRevoking}
              isDark={modal.isDark}
            />
          </Box>
        )}
    </ShareModalView>
  );
};

ReportShareSheetContainer.displayName = "ReportShareSheetContainer";

export default ReportShareSheetContainer;
